import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "feedback"]
  static values = {
    endpoint: String,
    targetVersion: String,
    fromVersion: String,
    returnTo: String,
    source: String,
    flagVariant: String,
    planContext: String,
    planId: String,
    cohort: String
  }

  // Per-browser flag: the user dismissed the "Try Planner V2" offer. Distinct
  // from the saved classic/v2 preference — dismissing only silences the nag,
  // it does not choose a planner. No server state, so it needs no migration.
  static DISMISS_KEY = "pl2.v1OfferDismissed"

  connect() {
    this.onKeydown = this.onKeydown.bind(this)
    this.resetButtons = this.resetButtons.bind(this)
    // A page restored from the bfcache or Turbo page cache can bring back a
    // stale disabled button (we disable it during an in-flight switch). Re-enable
    // on restore so the control never gets permanently stuck.
    window.addEventListener("pageshow", this.resetButtons)
    document.addEventListener("turbo:load", this.resetButtons)
    this.hideIfDismissed()
  }

  // Only the classic-planner offer carries this class; the v2→classic switch
  // shares this controller but must never be auto-hidden.
  hideIfDismissed() {
    if (!this.element.classList.contains("planner-version-offer")) return
    try {
      if (localStorage.getItem(this.constructor.DISMISS_KEY)) this.element.hidden = true
    } catch (_) { /* localStorage unavailable — just show the offer */ }
  }

  dismiss() {
    try { localStorage.setItem(this.constructor.DISMISS_KEY, "1") } catch (_) { /* ignore */ }
    window.posthog?.capture("planner_version_offer_dismissed", this.eventProperties())
    this.element.hidden = true
  }

  disconnect() {
    window.removeEventListener("pageshow", this.resetButtons)
    document.removeEventListener("turbo:load", this.resetButtons)
    document.removeEventListener("keydown", this.onKeydown)
  }

  openFeedback() {
    this.previouslyFocused = document.activeElement
    this.modalTarget.hidden = false
    this.modalTarget.style.display = "flex"
    document.addEventListener("keydown", this.onKeydown)
    this.feedbackTarget.focus()
  }

  cancelFeedback() {
    window.posthog?.capture("planner_version_switch_cancelled", this.eventProperties())
    this.closeModal()
  }

  closeFromBackdrop(event) {
    if (event.target === this.modalTarget) this.cancelFeedback()
  }

  // Escape closes the modal; Tab is kept within it so keyboard focus can't
  // wander to the planner canvas behind the dialog.
  onKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.cancelFeedback()
    } else if (event.key === "Tab") {
      this.trapFocus(event)
    }
  }

  trapFocus(event) {
    const focusable = this.modalTarget.querySelectorAll(
      'a[href], button:not([disabled]), textarea, input:not([disabled]), select, [tabindex]:not([tabindex="-1"])'
    )
    if (focusable.length === 0) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  async switchVersion(event) {
    const button = event.currentTarget
    button.disabled = true

    try {
      const response = await fetch(this.endpointValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({
          planner_version: this.targetVersionValue,
          return_to: this.returnToValue,
          feedback: this.hasFeedbackTarget ? this.feedbackTarget.value : "",
          ...this.eventProperties(),
          session_id: window.posthog?.get_session_id?.(),
          session_replay_url: window.posthog?.get_session_replay_url?.(),
          current_url: window.location.href
        })
      })

      if (!response.ok) throw new Error("Could not switch planner versions")
      const result = await response.json()
      window.location.assign(result.redirect_to)
    } catch (error) {
      button.disabled = false
      window.posthog?.captureException?.(error)
      window.alert("Could not switch planner versions. Please try again.")
    }
  }

  eventProperties() {
    return {
      from_version: this.fromVersionValue,
      to_version: this.targetVersionValue,
      source: this.sourceValue,
      flag_variant: this.flagVariantValue || null,
      planner_version_cohort: this.cohortValue,
      plan_context: this.planContextValue,
      plan_id: this.planIdValue || null
    }
  }

  resetButtons() {
    this.element.querySelectorAll("button[disabled]").forEach((button) => {
      button.disabled = false
    })
  }

  closeModal() {
    document.removeEventListener("keydown", this.onKeydown)
    this.modalTarget.hidden = true
    this.modalTarget.style.display = "none"
    this.previouslyFocused?.focus?.()
  }
}
